import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;   // 15 minutes
const IDLE_WARNING_MS = 14 * 60 * 1000;   // warn at 14 min

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);
  const idleTimer = useRef();
  const warnTimer = useRef();
  const warnedRef = useRef(false);

  const doLogout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { if (err?.response?.status !== 401) console.warn("logout call failed:", err?.message || err); }
    setUser(null);
  }, []);

  // On mount, ask the backend "who am I?" — cookie tells the truth. No client-side token cache.
  useEffect(() => {
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(null));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const logout = doLogout;

  // Idle-timeout auto-logout for authenticated sessions (HIPAA §164.312(a)(2)(iii))
  const resetIdle = useCallback(() => {
    clearTimeout(idleTimer.current);
    clearTimeout(warnTimer.current);
    warnedRef.current = false;
    warnTimer.current = setTimeout(() => {
      if (!warnedRef.current) {
        warnedRef.current = true;
        toast.warning("You'll be logged out in 60 seconds due to inactivity.", { duration: 10000 });
      }
    }, IDLE_WARNING_MS);
    idleTimer.current = setTimeout(() => {
      toast.error("Logged out due to inactivity.");
      doLogout();
    }, IDLE_TIMEOUT_MS);
  }, [doLogout]);

  useEffect(() => {
    if (!user) {
      clearTimeout(idleTimer.current);
      clearTimeout(warnTimer.current);
      return;
    }
    resetIdle();
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    let throttleTs = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - throttleTs < 2000) return;   // throttle to once per 2s
      throttleTs = now;
      resetIdle();
    };
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearTimeout(idleTimer.current);
      clearTimeout(warnTimer.current);
    };
  }, [user, resetIdle]);

  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
